<template>
  <form class="rh-filter" @submit.prevent="submit">
    <div class="rh-filter-field rh-filter-field-grow rh-filter-field-input">
      <label class="rh-filter-label" :for="cityInputId">Город</label>

      <!-- Customer-scoped city list: when the backend returned a finite set
           of cities the host operates in, render a static select; otherwise
           fall back to free-text autocomplete against /locations/cities. -->
      <select
        v-if="useCitySelect"
        :id="cityInputId"
        v-model="selectedCommonId"
        class="rh-filter-select"
        @change="onCitySelect"
      >
        <option :value="0" disabled>Выберите город</option>
        <option
          v-for="city in customerCities"
          :key="city.id"
          :value="city.commonCityId"
        >
          {{ city.name }}
        </option>
      </select>

      <template v-else>
        <input
          :id="cityInputId"
          v-model="cityQuery"
          class="rh-filter-input"
          type="text"
          autocomplete="off"
          placeholder="Например, Москва"
          @focus="onCityFocus"
          @input="onCityInput"
          @blur="onCityBlur"
        />
        <ul v-if="showSuggest && suggestions.length" class="rh-suggest-list">
          <li
            v-for="city in suggestions"
            :key="city.id"
            class="rh-suggest-item"
            @mousedown.prevent="pickCity(city)"
          >
            {{ city.name }}
          </li>
        </ul>
        <div
          v-else-if="showSuggest && cityQuery.trim()"
          class="rh-suggest-list"
        >
          <p class="rh-suggest-empty">Ничего не найдено</p>
        </div>
      </template>
    </div>

    <div class="rh-filter-field">
      <label class="rh-filter-label" :for="checkInId">Заезд</label>
      <input
        :id="checkInId"
        v-model="form.checkIn"
        class="rh-filter-input"
        type="date"
        :min="today"
        required
      />
    </div>

    <div class="rh-filter-field">
      <label class="rh-filter-label" :for="checkOutId">Выезд</label>
      <input
        :id="checkOutId"
        v-model="form.checkOut"
        class="rh-filter-input"
        type="date"
        :min="form.checkIn || today"
        required
      />
    </div>

    <div class="rh-filter-field">
      <label class="rh-filter-label" :for="guestsId">Взрослые</label>
      <input
        :id="guestsId"
        v-model.number="form.guests"
        class="rh-filter-input"
        type="number"
        min="1"
        max="20"
      />
    </div>

    <div class="rh-filter-field">
      <label class="rh-filter-label" :for="childrenId">Дети</label>
      <input
        :id="childrenId"
        v-model.number="form.children"
        class="rh-filter-input"
        type="number"
        min="0"
        max="10"
      />
    </div>

    <button class="rh-filter-submit" type="submit" :disabled="!canSearch">
      Найти
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';

import type { ApiClient, CityDTO } from '../api';
import { todayISO } from '../state';
import type { ResolvedSearchParams, WidgetCityOption } from '../types';

const props = defineProps<{
  initial: ResolvedSearchParams;
  api: ApiClient;
  customerCities: WidgetCityOption[];
  /** Искать по любому городу: вместо списка городов хоста — автокомплит. */
  allowAnyCity?: boolean;
}>();

const emit = defineEmits<{
  submit: [params: ResolvedSearchParams];
}>();

const uid = Math.random().toString(36).slice(2, 8);
const cityInputId = `rh-city-${uid}`;
const checkInId = `rh-in-${uid}`;
const checkOutId = `rh-out-${uid}`;
const guestsId = `rh-g-${uid}`;
const childrenId = `rh-c-${uid}`;

const today = todayISO();

const form = reactive({
  cityId: props.initial.cityId,
  rcId: props.initial.rcId,
  cityName: props.initial.cityName,
  commonCityId: props.initial.commonCityId,
  checkIn: props.initial.checkIn,
  checkOut: props.initial.checkOut,
  guests: props.initial.guests,
  children: props.initial.children,
});

const cityQuery = ref(props.initial.cityName ?? '');
const suggestions = ref<CityDTO[]>([]);
const showSuggest = ref(false);
const selectedCommonId = ref<number>(props.initial.commonCityId ?? 0);

let lookupToken = 0;
let debounceHandle: number | null = null;

// Pre-select the customer city that matches the initial config — if the
// embedder passed `city: 'Москва'` and the host has Moscow in their list,
// the dropdown opens with it already chosen.
watch(
  () => props.customerCities,
  (cities) => {
    if (!cities.length) return;
    if (selectedCommonId.value) return;
    const match = cities.find(
      (c) =>
        (props.initial.commonCityId &&
          c.commonCityId === props.initial.commonCityId) ||
        (props.initial.cityName &&
          c.name.toLowerCase() === props.initial.cityName.toLowerCase()),
    );
    if (match) {
      selectedCommonId.value = match.commonCityId;
      form.cityId = match.tlCityId;
      form.rcId = match.rcId;
      form.cityName = match.name;
      form.commonCityId = match.commonCityId;
    }
  },
  { immediate: true },
);

watch(
  () => props.initial,
  (next) => {
    form.cityId = next.cityId;
    form.rcId = next.rcId;
    form.cityName = next.cityName;
    form.commonCityId = next.commonCityId;
    form.checkIn = next.checkIn;
    form.checkOut = next.checkOut;
    form.guests = next.guests;
    form.children = next.children;
    cityQuery.value = next.cityName ?? '';
    selectedCommonId.value = next.commonCityId ?? 0;
  },
  { deep: true },
);

// Список городов хоста — только когда поиск ограничен ими.
const useCitySelect = computed(
  () => props.customerCities.length > 0 && !props.allowAnyCity,
);

const hasCity = computed(() => {
  if (useCitySelect.value) return selectedCommonId.value > 0;
  return cityQuery.value.trim().length > 0;
});

const canSearch = computed(
  () =>
    hasCity.value &&
    !!form.checkIn &&
    !!form.checkOut &&
    form.checkIn < form.checkOut &&
    form.guests >= 1 &&
    form.children >= 0,
);

async function lookupCities(q: string): Promise<void> {
  const token = ++lookupToken;
  try {
    // Пустой запрос при разрешённом поиске по любому городу: сначала города
    // самого хоста — по ним у гостя есть чем выбирать.
    const cities =
      !q.trim() && props.customerCities.length
        ? props.customerCities.map((c) => ({
            id: c.id,
            name: c.name,
            tl_city_id: c.tlCityId,
            common_id: c.commonCityId,
          }))
        : await props.api.findCity(q);
    if (token !== lookupToken) return;
    suggestions.value = cities;
  } catch {
    if (token === lookupToken) suggestions.value = [];
  }
}

function onCityFocus(): void {
  showSuggest.value = true;
  if (!suggestions.value.length) void lookupCities(cityQuery.value);
}

function onCityBlur(): void {
  window.setTimeout(() => {
    showSuggest.value = false;
  }, 120);
}

function onCityInput(): void {
  if (debounceHandle !== null) window.clearTimeout(debounceHandle);
  debounceHandle = window.setTimeout(() => {
    void lookupCities(cityQuery.value);
  }, 200);
}

function pickCity(city: CityDTO): void {
  form.cityId = city.tl_city_id ?? null;
  form.rcId = city.rc_id ?? null;
  form.cityName = city.name;
  form.commonCityId = city.common_id ?? city.id;
  cityQuery.value = city.name;
  showSuggest.value = false;
}

function onCitySelect(): void {
  const city = props.customerCities.find(
    (c) => c.commonCityId === selectedCommonId.value,
  );
  if (!city) return;
  form.cityId = city.tlCityId;
  form.rcId = city.rcId;
  form.cityName = city.name;
  form.commonCityId = city.commonCityId;
}

async function submit(): Promise<void> {
  if (!canSearch.value) return;

  // Free-text input path: resolve the typed query before submitting so the
  // search call uses a concrete commonCityId rather than silently falling
  // back to "all cities" filtered by date.
  if (!useCitySelect.value) {
    const typed = cityQuery.value.trim();
    const needsResolve =
      typed &&
      (!form.cityName || form.cityName !== typed || !form.commonCityId);
    if (needsResolve) {
      try {
        const cities = await props.api.findCity(typed);
        const exact = cities.find(
          (c) => c.name.toLowerCase() === typed.toLowerCase(),
        );
        const match = exact ?? cities[0] ?? null;
        if (match) {
          form.cityId = match.tl_city_id ?? null;
          form.rcId = match.rc_id ?? null;
          form.cityName = match.name;
          form.commonCityId = match.common_id ?? match.id;
          cityQuery.value = match.name;
        }
      } catch {
        // network blip — submit with the partial state we already have
      }
    }
  }

  const childrenAges =
    props.initial.childrenAges &&
    props.initial.childrenAges.length === form.children
      ? props.initial.childrenAges
      : null;

  emit('submit', {
    ...props.initial,
    cityId: form.cityId,
    rcId: form.rcId,
    cityName: form.cityName,
    commonCityId: form.commonCityId,
    checkIn: form.checkIn,
    checkOut: form.checkOut,
    guests: form.guests,
    children: form.children,
    childrenAges,
    page: 1,
  });
}
</script>
